import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, input, model, TemplateRef } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FormValueControl } from '@angular/forms/signals'
import { MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'

/**
 * Ein Select-Feld im Material Design.
 *
 * **`FormValueControl`**: der Wert liegt in einem `model()`. Damit laesst sich die Komponente an
 * einen Signal-Forms-Feldbaum binden (`[formField]="fg.feld"`) **und** ohne jedes Formular
 * verwenden (`[(value)]="meinSignal"`). Vorher nahm sie ein fertiges `FormControl` entgegen
 * (`passthroughFormControl`) — das war der Grund, warum jeder Aufrufer ein Reactive-Forms-Control
 * halten musste, auch wenn er sonst laengst auf Signal Forms lag.
 */
@Component({
  selector: 'lib-select',
  imports: [FormsModule, CommonModule, MatSelectModule, MatFormFieldModule],
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './select.component.scss',
})
export class SelectComponent<Item> implements FormValueControl<Item | null> {
  label = input('')

  readonly value = model<Item | null>(null)

  /** Von Signal Forms gespiegelt, wenn eine `disabled(...)`-Regel auf dem Feld liegt. */
  readonly disabled = input(false)

  /** 'dynamic' in Formularen, die ihre Abstaende selbst per gap setzen statt ueber die Subscript-Reserve */
  readonly subscriptSizing = input<SubscriptSizing>('dynamic')

  /**
   * Wählbare Optionen
   */
  options = input<Item[] | undefined>([])

  /**
   * Template für die Anzeige im Dropdown Menu (Option wird als $implicit übergeben)
   */
  displayTemplate = input.required<TemplateRef<unknown>>()

  /**
   * Multiple Auswahl erlauben
   */
  multiple = input(false)

  /**
   * Entscheidet je Option, ob sie waehlbar ist. Standard: alles waehlbar.
   *
   * Gedacht fuer Werte, die es weiterhin GIBT, aber nicht mehr neu gewaehlt werden sollen — etwa
   * eine fusionierte Krankenkasse. Die Option bleibt sichtbar, damit ein Bestandsdatensatz seinen
   * Wert weiter anzeigt; sie herauszufiltern wuerde ihn im Feld leer aussehen lassen und beim
   * naechsten Speichern still verlieren.
   */
  isOptionDisabled = input<(option: Item) => boolean>(() => false)
}
